/** Independent scene, query, and evidence state for NeuroViz Explorer. */

export const REQUEST_STATES = Object.freeze(['idle', 'loading', 'ready', 'empty', 'unavailable', 'unauthorized', 'processing', 'error']);

const initialChannel = () => ({ status: 'idle', requestId: 0, data: null, error: null });

export const initialExplorerState = Object.freeze({
  datasetId: null,
  version: null,
  coordinateSpace: null,
  cohort: null,
  colorMode: 'taxonomy',
  colorGeneId: null,
  activeView: 'scene',
  resultQuery: { page: 1, page_size: 25, sort_by: 'entity_id', sort_direction: 'asc' },
  camera: { target: [0, 0, 0], position: [0, 0, 1], zoom: 1 },
  layers: { atlas: { visible: true, opacity: 0.35 }, pointCloud: { visible: true, opacity: 1 }, domain: { visible: false, opacity: 0.16 } },
  filters: {},
  resultCount: 0,
  selectedEntityId: null,
  hover: null,
  selection: null,
  scene: initialChannel(),
  query: initialChannel(),
  evidence: initialChannel(),
});

export const createInitialExplorerState = () => ({
  ...initialExplorerState,
  camera: { ...initialExplorerState.camera },
  layers: { atlas: { visible: true, opacity: 0.35 }, pointCloud: { visible: true, opacity: 1 }, domain: { visible: false, opacity: 0.16 } },
  filters: {},
  scene: initialChannel(),
  query: initialChannel(),
  evidence: initialChannel(),
});

export const DEFAULT_CAMERA = Object.freeze({ target: [0, 0, 0], position: [0, 0, 1], zoom: 1, rotation: 0, pan: [0, 0] });

const finiteNumber = (value, fallback) => (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) && Number.isFinite(Number(value)) && Math.abs(Number(value)) <= 1e9 ? Number(value) : fallback;
const finiteVector = (value, length, fallback) => Array.from({ length }, (_, index) => finiteNumber(value?.[index], fallback[index]));

/** Keep camera updates serialisable so they can be shared by Deck and vtk. */
export const normalizeCamera = (input = {}) => {
  const camera = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  return ({
  ...DEFAULT_CAMERA,
  target: finiteVector(camera.target, 3, DEFAULT_CAMERA.target),
  position: finiteVector(camera.position, 3, DEFAULT_CAMERA.position),
  pan: finiteVector(camera.pan, 2, DEFAULT_CAMERA.pan),
  zoom: finiteNumber(camera.zoom, DEFAULT_CAMERA.zoom),
  rotation: finiteNumber(camera.rotation, DEFAULT_CAMERA.rotation),
  ...(Array.isArray(camera.viewUp) ? {viewUp:finiteVector(camera.viewUp,3,[0,1,0]),viewAngle:finiteNumber(camera.viewAngle,30),parallelScale:finiteNumber(camera.parallelScale,1)} : {}),
  ...(typeof camera.frameId === 'string' && /^[A-Za-z0-9_.:-]{1,128}$/.test(camera.frameId) ? {frameId:camera.frameId} : {}),
});
};

export const cameraAction = (camera, action) => {
  const current = normalizeCamera(camera);
  if (action === 'reset') return normalizeCamera(action.camera || DEFAULT_CAMERA);
  if (action === 'zoomIn' || action === 'zoomOut') return { ...current, zoom: Math.max(.05, Math.min(32, current.zoom * (action === 'zoomIn' ? 1.25 : .8))) };
  if (action === 'rotateLeft' || action === 'rotateRight') return { ...current, rotation: current.rotation + (action === 'rotateLeft' ? -15 : 15) };
  const delta = { up: [0, -1], down: [0, 1], left: [-1, 0], right: [1, 0] }[action];
  return delta ? { ...current, pan: [current.pan[0] + delta[0], current.pan[1] + delta[1]] } : current;
};

const copy = (value) => ({ ...value });
const channelFor = (state, channel) => state[channel];
const isCurrent = (state, channel, requestId) => channelFor(state, channel).requestId === requestId;
/** Resolve every selection source to the API's durable entity_id. */
export const stableEntity = (value) => {
  if (typeof value === 'string' && value) return value;
  if (value && typeof value.entity_id === 'string' && value.entity_id) return value.entity_id;
  return null;
};

/** Read/write selection without losing unrelated URL state. */
export const readEntitySelection = (location = typeof window !== 'undefined' ? window.location : null) => {
  if (!location) return null;
  const params = new URL(location.href).searchParams;
  return stableEntity(params.get('entity_id') || params.get('entity'));
};

export const writeEntitySelection = (entity, history = typeof window !== 'undefined' ? window.history : null, location = typeof window !== 'undefined' ? window.location : null) => {
  if (!history || !location) return;
  const url = new URL(location.href);
  const entityId = stableEntity(entity);
  if (entityId) url.searchParams.set('entity_id', entityId);
  else url.searchParams.delete('entity_id');
  if (url.href !== location.href) history.replaceState({}, '', url);
};

// URL state is deliberately an allow-list.  In particular, never serialise a
// manifest/asset object: those objects may contain protected storage URIs.
export const URL_FILTER_KEYS = Object.freeze(['entity_type', 'region', 'layer', 'class_id', 'subclass_id', 'gene_min', 'gene_max', 'gene_range', 'region_id', 'region_query', 'sample_id', 'cluster_id', 'gene_id', 'quality_min', 'quality_max', 'metric_key']);
const URL_QUERY_KEYS = Object.freeze(['page', 'page_size', 'sort_by', 'sort_direction']);
const DEFAULT_FILTERS = Object.freeze(Object.fromEntries(URL_FILTER_KEYS.map((key) => [key, ''])));
const isSafeUrlValue = (value) => typeof value === 'string' && value.length <= 512 && !/^(?:https?:|ftp:|file:|data:|blob:|javascript:|s3:)/i.test(value) && !/[\\/]{2,}/.test(value);
const safeFilterValue = (value) => value && typeof value === 'object' ? (JSON.stringify(value).length <= 1024 ? value : null) : isSafeUrlValue(String(value ?? '')) ? String(value ?? '') : null;
const layerDefaults = { atlas: .35, pointCloud: 1, domain: .16 };
const safeLayer = (key, layer) => ({
  visible: typeof layer.visible === 'boolean' ? layer.visible : key !== 'domain',
  opacity: Math.max(0, Math.min(1, finiteNumber(layer.opacity, layerDefaults[key]))),
});
const parseJson = (value, fallback) => {
  if (!value) return fallback;
  try { return JSON.parse(value); } catch { return fallback; }
};
const safeJson = (value, fallback) => {
  try {
    const encoded = JSON.stringify(value);
    return encoded && encoded.length <= 4096 ? encoded : fallback;
  } catch { return fallback; }
};

export const datasetPath = (datasetId, version) => `/explorer/${encodeURIComponent(datasetId)}/${encodeURIComponent(version)}/`;
export const datasetRoute = (location) => {
  const parts = new URL(location.href).pathname.match(/^\/explorer\/([^/]+)\/([^/]+)\/$/);
  if (!parts || parts[1] === 'api') return null;
  try { return {datasetId:decodeURIComponent(parts[1]),version:decodeURIComponent(parts[2])}; }
  catch { return null; }
};

export const readExplorerUrlState = (location = typeof window !== 'undefined' ? window.location : null) => {
  if (!location) return {};
  const params = new URL(location.href).searchParams;
  const state = {};
  state.urlErrors = [];
  for (const key of ['camera', 'layers']) {
    if (!params.has(key)) continue;
    const value = parseJson(params.get(key), null);
    if (!value || typeof value !== 'object' || Array.isArray(value)) state.urlErrors.push(key);
    else if (key === 'camera' && Object.entries(value).some(([field, item]) => ['target', 'position', 'pan', 'viewUp'].includes(field)
      ? !Array.isArray(item) || item.length !== (field === 'pan' ? 2 : 3) || item.some((n) => finiteNumber(n, null) === null)
      : field === 'frameId' ? typeof item !== 'string' || !/^[A-Za-z0-9_.:-]{1,128}$/.test(item) : finiteNumber(item, null) === null)) state.urlErrors.push(key);
    else if (key === 'layers' && Object.values(value).some((item) => !item || typeof item.visible !== 'boolean' || finiteNumber(item.opacity, null) === null || item.opacity < 0 || item.opacity > 1)) state.urlErrors.push(key);
  }
  const cohort = parseJson(params.get('cohort'), null);
  if (cohort && typeof cohort === 'object') state.cohort = Object.fromEntries(['id', 'release_id', 'selection_checksum', 'bundle_sha256'].filter((key) => cohort[key] !== undefined && isSafeUrlValue(String(cohort[key]))).map((key) => [key, cohort[key]]));
  state.activeView = params.get('view') === 'data' ? 'data' : 'scene';
  state.colorMode = ['taxonomy', 'gene', 'region'].includes(params.get('color_mode')) ? params.get('color_mode') : 'taxonomy';
  if (isSafeUrlValue(params.get('color_gene_id'))) state.colorGeneId = params.get('color_gene_id');
  const share = params.get('share');
  // Signed, compressed state is larger than a stable entity ID. Keep the
  // server's bounded token envelope instead of silently treating it as absent.
  if (share && share.length <= 24000 && /^[A-Za-z0-9_.:-]+$/.test(share)) state.shareToken = share;
  else if (params.has('share')) state.urlErrors.push('share');
  const datasetId = params.get('dataset_id');
  const version = params.get('version');
  const coordinateSpace = params.get('coordinate_space');
  if (isSafeUrlValue(datasetId)) state.datasetId = datasetId;
  if (isSafeUrlValue(version)) state.version = version;
  if (isSafeUrlValue(coordinateSpace)) state.coordinateSpace = coordinateSpace;
  const entityId = stableEntity(params.get('entity_id') || params.get('entity'));
  if (entityId && isSafeUrlValue(entityId)) state.selectedEntityId = entityId;
  const filters = parseJson(params.get('filters'), null);
  if (filters && typeof filters === 'object' && !Array.isArray(filters)) {
    state.filters = Object.fromEntries(URL_FILTER_KEYS.map((key) => [key, safeFilterValue(filters[key])]).filter(([, value]) => value !== null && value !== ''));
  }
  const layers = parseJson(params.get('layers'), null);
  if (layers && typeof layers === 'object' && !Array.isArray(layers)) {
    state.layers = Object.fromEntries(['atlas', 'pointCloud', 'domain'].filter((key) => layers[key] && typeof layers[key] === 'object' && !Array.isArray(layers[key])).map((key) => [key, safeLayer(key, layers[key])]));
  }
  const camera = parseJson(params.get('camera'), null);
  if (camera && typeof camera === 'object' && !Array.isArray(camera)) state.camera = normalizeCamera(camera);
  else if (params.has('camera')) state.camera = normalizeCamera();
  URL_FILTER_KEYS.forEach((key) => { if (params.has(key)) state.filters = { ...state.filters, [key]: safeFilterValue(params.get(key)) }; });
  const query = Object.fromEntries(URL_QUERY_KEYS.filter((key) => params.has(key)).map((key) => {
    if (key === 'page' || key === 'page_size') return [key, Math.max(1, Math.min(key === 'page' ? 10000 : 100, Math.floor(Number(params.get(key))) || 1))];
    return [key, safeFilterValue(params.get(key))];
  }).filter(([, value]) => value !== null && value !== ''));
  if (Object.keys(query).length) state.resultQuery = query;
  const route = datasetRoute(location);
  if (route) {
    if ((state.datasetId && state.datasetId !== route.datasetId) || (state.version && state.version !== route.version)) state.urlErrors.push('dataset_route');
    Object.assign(state, route);
  }
  return state;
};

export const writeExplorerUrlState = (state = {}, history = typeof window !== 'undefined' ? window.history : null, location = typeof window !== 'undefined' ? window.location : null) => {
  if (!history || !location) return;
  const url = new URL(location.href);
  const setOrDelete = (key, value) => { if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value)); else url.searchParams.delete(key); };
  setOrDelete('dataset_id', isSafeUrlValue(state.datasetId) ? state.datasetId : null);
  if (state.datasetId) url.searchParams.delete('share');
  setOrDelete('version', isSafeUrlValue(state.version) ? state.version : null);
  setOrDelete('cohort', state.cohort ? safeJson(Object.fromEntries(['id', 'release_id', 'selection_checksum', 'bundle_sha256'].filter((key) => state.cohort[key] !== undefined).map((key) => [key, state.cohort[key]])), null) : null);
  setOrDelete('view', state.activeView === 'data' ? 'data' : 'scene');
  setOrDelete('color_mode', ['taxonomy', 'gene', 'region'].includes(state.colorMode) ? state.colorMode : null);
  setOrDelete('color_gene_id', isSafeUrlValue(state.colorGeneId) ? state.colorGeneId : null);
  url.searchParams.delete('entity');
  URL_FILTER_KEYS.forEach((key) => url.searchParams.delete(key));
  setOrDelete('coordinate_space', isSafeUrlValue(state.coordinateSpace) ? state.coordinateSpace : null);
  const entityId = stableEntity(state.selectedEntityId || state.entityId);
  setOrDelete('entity_id', entityId && isSafeUrlValue(entityId) ? entityId : null);
  const filters = Object.fromEntries(URL_FILTER_KEYS.map((key) => [key, safeFilterValue(state.filters?.[key])]).filter(([, value]) => value !== null && value !== ''));
  const layers = state.layers && safeJson(Object.fromEntries(['atlas', 'pointCloud', 'domain'].filter((key) => state.layers[key] && typeof state.layers[key] === 'object').map((key) => [key, safeLayer(key, state.layers[key])])), null);
  const normalizedCamera = state.camera && normalizeCamera(state.camera);
  const camera = normalizedCamera && safeJson(normalizedCamera, null);
  setOrDelete('filters', Object.keys(filters).length ? safeJson(filters, null) : null);
  setOrDelete('layers', layers);
  setOrDelete('camera', camera);
  URL_QUERY_KEYS.forEach((key) => {
    const value = key === 'page' || key === 'page_size'
      ? Math.max(1, Math.min(key === 'page' ? 10000 : 100, Math.floor(Number(state.resultQuery?.[key])) || 1))
      : safeFilterValue(state.resultQuery?.[key]);
    setOrDelete(key, value);
  });
  if (url.href !== location.href && url.href.length <= 16384) history.replaceState({}, '', url);
};
const responseStatus = (data) => {
  const candidate = data?.result_state || data?.state;
  if (REQUEST_STATES.includes(candidate)) return candidate;
  return data?.count === 0 ? 'empty' : 'ready';
};

/** @param {object} state @param {object} action */
export function explorerReducer(state = initialExplorerState, action) {
  switch (action.type) {
    case 'SET_DATASET': return { ...state, datasetId: action.datasetId, version: action.version || null, coordinateSpace: action.coordinateSpace || null, cohort: action.cohort || null, selection: null, selectedEntityId: null, hover: null, scene: initialChannel(), query: initialChannel(), evidence: initialChannel() };
    case 'SET_COORDINATE_SPACE': return { ...state, coordinateSpace: action.coordinateSpace || null };
    case 'SET_CAMERA': return { ...state, camera: normalizeCamera({ ...state.camera, ...action.camera }) };
    case 'CAMERA_ACTION': return { ...state, camera: cameraAction(state.camera, action.action) };
    case 'RESET_CAMERA': return { ...state, camera: normalizeCamera(action.camera) };
    case 'SET_LAYERS': return { ...state, layers: { ...state.layers, ...action.layers } };
    case 'SET_FILTERS': return { ...state, filters: { ...action.filters }, resultQuery: { ...state.resultQuery, page: 1 } };
    case 'SET_COHORT': return { ...state, cohort: action.cohort };
    case 'SET_ENCODING': return { ...state, colorMode: action.colorMode, colorGeneId: action.colorGeneId || null };
    case 'SET_ACTIVE_VIEW': return { ...state, activeView: action.activeView === 'data' ? 'data' : 'scene' };
    case 'SET_RESULT_QUERY': return { ...state, resultQuery: { ...state.resultQuery, ...action.query } };
    case 'SET_HOVER': return { ...state, hover: stableEntity(action.entity) };
    case 'SET_SELECTION': {
      const selectedEntityId = stableEntity(action.entity);
      return { ...state, selection: selectedEntityId, selectedEntityId };
    }
    case 'REQUEST_START': return { ...state, [action.channel]: { status: 'loading', requestId: action.requestId, data: null, error: null } };
    case 'REQUEST_SUCCESS': {
      if (!isCurrent(state, action.channel, action.requestId)) return state;
      // Evidence belongs to the selection that initiated it. A request can
      // still resolve after a newer click even when it was not abortable.
      if (action.channel === 'evidence' && action.entityId && action.entityId !== state.selectedEntityId) return state;
      const status = action.status || responseStatus(action.data);
      const next = { ...state, [action.channel]: { status, requestId: action.requestId, data: action.data, error: null } };
      if (action.channel === 'query') next.resultCount = Number(action.data?.count || 0);
      return next;
    }
    case 'REQUEST_ERROR': {
      if (!isCurrent(state, action.channel, action.requestId)) return state;
      if (action.channel === 'evidence' && action.entityId && action.entityId !== state.selectedEntityId) return state;
      return { ...state, [action.channel]: { ...channelFor(state, action.channel), status: action.status || 'error', error: action.error, data: null } };
    }
    case 'REQUEST_ABORT': return isCurrent(state, action.channel, action.requestId) ? { ...state, [action.channel]: { ...channelFor(state, action.channel), status: 'idle' } } : state;
    default: return state;
  }
}

/** A tiny store with independently abortable requests. */
export function createExplorerStore({ client, initialState = createInitialExplorerState() } = {}) {
  let state = { ...initialState, scene: copy(initialState.scene), query: copy(initialState.query), evidence: copy(initialState.evidence) };
  let sequence = 0;
  const controllers = new Map();
  const listeners = new Set();
  const getState = () => state;
  const dispatch = (action) => { state = explorerReducer(state, action); listeners.forEach((listener) => listener(state)); return state; };
  const subscribe = (listener) => { listeners.add(listener); return () => listeners.delete(listener); };
  const run = async (channel, operation, entityId = null) => {
    const requestId = ++sequence;
    controllers.get(channel)?.abort();
    const controller = new AbortController();
    controllers.set(channel, controller);
    dispatch({ type: 'REQUEST_START', channel, requestId });
    try {
      const data = await operation(controller.signal);
      if (controller.signal.aborted) return undefined;
      dispatch({ type: 'REQUEST_SUCCESS', channel, requestId, entityId, data });
      return data;
    } catch (error) {
      if (error?.name === 'AbortError') { dispatch({ type: 'REQUEST_ABORT', channel, requestId }); return undefined; }
      const status = error?.status === 401 || error?.status === 403
        ? 'unauthorized'
        : error?.status === 404
          ? 'unavailable'
          : error?.status === 202
            ? 'processing'
            : 'error';
      dispatch({ type: 'REQUEST_ERROR', channel, requestId, entityId, status, error });
      throw error;
    }
  };
  return {
    getState,
    dispatch,
    subscribe,
    run,
    loadScene: (operation) => run('scene', operation),
    loadQuery: (operation) => run('query', operation),
    loadEvidence: (operation, entityId = null) => run('evidence', operation, stableEntity(entityId)),
    cancel: (channel) => controllers.get(channel)?.abort(),
  };
}

/** Full collections use this durable shape; handles, pages and tiles live elsewhere. */
export const COLLECTION_FILTER_LISTS = ['region_ids', 'class_ids', 'subclass_ids', 'layers', 'sections'];
const collectionError = (field) => Object.assign(new Error(`Invalid collection ${field}`), { code: 'invalid_collection_state', field });
const requireCollection = (condition, field) => { if (!condition) throw collectionError(field); };
export function normalizeCollectionState(input) {
  requireCollection(input && typeof input === 'object', 'state');
  requireCollection(input.schema_version === undefined || input.schema_version === 'collection-share/v1', 'schema_version');
  const identifier = (value) => typeof value === 'string' && value.length > 0 && value.length <= 128;
  requireCollection(identifier(input.datasetId) && identifier(input.version), 'identity');
  const pins = input.collection || {};
  const collection = {};
  for (const key of ['collection_id', 'release_id', 'source_release_id', 'content_sha256', 'cell_ids_sha256']) {
    requireCollection(identifier(pins[key]) && (!key.endsWith('sha256') || /^[a-f0-9]{64}$/.test(pins[key])), key);
    collection[key] = pins[key];
  }
  const source = input.filters || {};
  const allowed = [...COLLECTION_FILTER_LISTS, 'region_descendants', 'layer_applicability', 'quality_min', 'quality_max', 'gene_id', 'gene_min', 'gene_max', 'gene_positive_only', 'viewport', 'viewport_scientific_filter', 'coordinate_space_id'];
  requireCollection(typeof source === 'object' && !Array.isArray(source) && Object.keys(source).every(key => allowed.includes(key)), 'filters');
  const filters = {};
  for (const key of COLLECTION_FILTER_LISTS) {
    if (source[key] === undefined) continue;
    requireCollection(Array.isArray(source[key]) && source[key].length <= 100 && source[key].every(identifier), key);
    if (source[key].length) filters[key] = [...new Set(source[key])].sort();
  }
  requireCollection(source.region_descendants === undefined || typeof source.region_descendants === 'boolean', 'region_descendants');
  if (filters.region_ids) filters.region_descendants = source.region_descendants ?? true;
  requireCollection(['any', 'applicable', 'not_applicable', 'unknown'].includes(source.layer_applicability ?? 'any'), 'layer_applicability');
  if (source.layer_applicability && source.layer_applicability !== 'any') filters.layer_applicability = source.layer_applicability;
  for (const prefix of ['quality', 'gene']) {
    for (const bound of ['min', 'max']) {
      const key = `${prefix}_${bound}`;
      if (source[key] === undefined) continue;
      requireCollection(typeof source[key] === 'number' && Number.isFinite(source[key]), key);
      filters[key] = source[key] === 0 ? 0 : source[key];
    }
    requireCollection((filters[`${prefix}_min`] ?? -Infinity) <= (filters[`${prefix}_max`] ?? Infinity), prefix);
  }
  if (source.gene_id !== undefined) { requireCollection(identifier(source.gene_id), 'gene_id'); filters.gene_id = source.gene_id; }
  requireCollection(!('gene_min' in filters || 'gene_max' in filters) || !!filters.gene_id, 'gene_id');
  if (source.gene_positive_only !== undefined) { requireCollection(typeof source.gene_positive_only === 'boolean' && !!filters.gene_id, 'gene_positive_only'); filters.gene_positive_only = source.gene_positive_only; }
  requireCollection(source.viewport_scientific_filter === undefined || typeof source.viewport_scientific_filter === 'boolean', 'viewport_scientific_filter');
  if (source.viewport_scientific_filter) {
    requireCollection(Array.isArray(source.viewport) && source.viewport.length === 6 && source.viewport.every(n => typeof n === 'number' && Number.isFinite(n)) && source.viewport.slice(0,3).every((n,i) => n <= source.viewport[i+3]) && identifier(source.coordinate_space_id), 'viewport');
    Object.assign(filters, {viewport: [...source.viewport], viewport_scientific_filter: true, coordinate_space_id: source.coordinate_space_id});
  } else requireCollection(source.viewport === undefined && source.coordinate_space_id === undefined, 'viewport_scientific_filter');
  requireCollection(identifier(input.frame?.frame_id) && identifier(input.frame?.units), 'frame');
  const frame = {frame_id: input.frame.frame_id, units: input.frame.units};
  if (input.camera !== undefined) {
    requireCollection(input.camera && typeof input.camera === 'object' && !Array.isArray(input.camera), 'camera');
    for (const [key, value] of Object.entries(input.camera)) {
      if (key === 'frameId') requireCollection(identifier(value), 'camera');
      else if (['position','target','viewUp','pan'].includes(key)) requireCollection(Array.isArray(value) && value.length === (key === 'pan' ? 2 : 3) && value.every(n => typeof n === 'number' && Number.isFinite(n) && Math.abs(n) <= 1e9), 'camera');
      else requireCollection(['zoom','rotation','viewAngle','parallelScale'].includes(key) && typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= 1e9, 'camera');
    }
    requireCollection(input.camera.viewAngle === undefined || (input.camera.viewAngle > 0 && input.camera.viewAngle < 180), 'camera');
    requireCollection(input.camera.parallelScale === undefined || input.camera.parallelScale > 0, 'camera');
  }
  requireCollection(input.detailIntent === undefined || ['overview','expression','provenance'].includes(input.detailIntent), 'detailIntent');
  const camera = normalizeCamera(input.camera);
  for (const key of ['viewAngle','parallelScale']) if (input.camera?.[key] !== undefined) camera[key] = input.camera[key];
  // A pose in a different (or unspecified) frame must never be replayed.
  const restoredCamera = input.camera?.frameId === frame.frame_id ? camera : {...normalizeCamera(), frameId: frame.frame_id};
  const order = input.tableOrder || {sort: 'entity_id'};
  requireCollection(typeof order.sort === 'string' && ['entity_id','anatomy','region','taxonomy','class','subclass','layer','quality','gene'].includes(order.sort.replace(/^-/, '')), 'tableOrder');
  requireCollection(order.sort.replace(/^-/, '') !== 'gene' || identifier(order.sort_gene_id), 'sort_gene_id');
  requireCollection(input.selectedEntityId == null || identifier(input.selectedEntityId), 'selectedEntityId');
  requireCollection(input.colorGeneId == null || identifier(input.colorGeneId), 'colorGeneId');
  requireCollection(input.display_limit === undefined || (Number.isInteger(input.display_limit) && input.display_limit >= 1 && input.display_limit <= 1000000), 'display_limit');
  const normalized = {
    schema_version: 'collection-share/v1', datasetId: input.datasetId, version: input.version, collection, filters, frame,
    selectedEntityId: input.selectedEntityId || null, colorMode: ['taxonomy','gene','region'].includes(input.colorMode) ? input.colorMode : 'taxonomy',
    colorGeneId: input.colorGeneId || null, activeView: input.activeView === 'data' ? 'data' : 'scene',
    tableOrder: {sort: order.sort, ...(order.sort_gene_id ? {sort_gene_id: order.sort_gene_id} : {})},
    camera: restoredCamera,
    layers: Object.fromEntries(Object.keys(layerDefaults).map(key => [key, safeLayer(key, input.layers?.[key] || {})])),
    ...(input.display_limit === undefined ? {} : {display_limit: input.display_limit}),
    ...(input.detailIntent ? {detailIntent: input.detailIntent} : {}),
  };
  requireCollection(JSON.stringify(normalized).length <= 16384, 'size');
  return normalized;
}

export function readCollectionUrlState(location) {
  const params = new URL(location.href).searchParams;
  if (!params.has('collection_state')) return {state: null, urlErrors: []};
  try {
    const value = params.get('collection_state');
    requireCollection(value.length <= 16384, 'size');
    return {state: normalizeCollectionState(JSON.parse(value)), urlErrors: []};
  } catch { return {state: null, urlErrors: ['collection_state']}; }
}

/** Only callers' settled interactions schedule writes. popstate cancels pending writes. */
export function createCollectionUrlSync({history, location, events, restore, delay = 200}) {
  let timer = null;
  let disposed = false;
  let restoring = false;
  const cancel = () => { clearTimeout(timer); timer = null; };
  const pop = () => {
    cancel(); restoring = true;
    try { restore(readCollectionUrlState(location)); } finally { restoring = false; }
  };
  events.addEventListener('popstate', pop);
  return {
    settled(state) {
      if (disposed || restoring) return;
      cancel();
      const encoded = JSON.stringify(normalizeCollectionState(state));
      if (encoded.length > 16384) throw collectionError('size');
      timer = setTimeout(() => {
        timer = null;
        const url = new URL(location.href);
        url.searchParams.set('collection_state', encoded);
        // Keep signed exact shares and unrelated/legacy URL parameters intact.
        if (url.href.length <= 32768 && url.href !== location.href) history.replaceState(history.state, '', url);
      }, delay);
    },
    dispose() { disposed = true; cancel(); events.removeEventListener('popstate', pop); },
  };
}
